import React, { useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faHeart } from '@fortawesome/free-solid-svg-icons'

// Translucent overlay rendered on top of the panel collection while a
// payment submit is in flight. Mirrors v6's Backdrop pattern: keeps the
// underlying step mounted (so hook state, Stripe Elements scope, and
// wallet sheets behind / above it all stay alive) but visually replaces
// it with a "Processing..." indicator. App.jsx applies `inert=""` to
// the panel container when this overlay shows so focus, pointer events,
// and a11y are all suppressed on the content beneath without us having
// to disable each interactive control individually.
//
// Used uniformly across credit_card / bank / wallet / paypal / deferred
// flows. Especially important for ExpressCheckoutElement where there's
// no built-in disabled state for the wallet button — without this
// overlay, the donor sees an active wallet button while the
// post-confirmPayment finalize / subscription PATCH runs (up to ~8s
// for subscriptions).
//
// Owns its own fade-in / fade-out lifecycle so the parent can just
// flip a `show` boolean and let the overlay stay mounted long enough
// for the animation to play out. Same three-phase pattern v6's
// Backdrop uses: phase null = unmounted, 'fade' = rendered at opacity
// 0 (one-tick window before transitioning), 'show' = visible. On
// `show=false` we transition back to 'fade' (opacity 0) and unmount
// after the transition duration.
const TRANSITION_MS = 300

const ProcessingOverlay = ({ i18n, show }) => {
  const [ phase, setPhase ] = useState(null)

  useEffect(() => {
    if (show) {
      // Render at opacity 0 first, then transition to opacity 1 on
      // the next tick so the browser has committed the initial paint
      // and the transition has a from/to to animate between. Same
      // 10ms two-step v6's Backdrop uses.
      setPhase('fade')
      const t = setTimeout(() => setPhase('show'), 10)
      return () => clearTimeout(t)
    }
    setPhase(p => (p ? 'fade' : null))
    const t = setTimeout(() => setPhase(null), TRANSITION_MS + 10)
    return () => clearTimeout(t)
  }, [ show ])

  if (!phase) return null

  const visible = phase === 'show'

  return (
    <div
      // role="status" + aria-live="polite" announces the
      // "Processing..." copy on the overlay's mount without
      // interrupting whatever the donor's screen reader was already
      // saying. role="status" implies aria-live="polite" but we set
      // both explicitly — Safari + VoiceOver have historically been
      // inconsistent about honoring the implicit form. inert on the
      // panel beneath (in App.jsx) suppresses the underlying tree's
      // a11y so the overlay's announcement isn't competing with the
      // step's heading / form labels.
      role="status"
      aria-live="polite"
      className="absolute inset-0 z-30 -m-3 p-3 flex items-center justify-center bg-white/85 backdrop-blur-sm transition-opacity"
      style={{
        opacity: visible ? 1 : 0,
        transitionDuration: `${TRANSITION_MS}ms`,
      }}
    >
      <div
        className="text-center py-6 transition-transform"
        style={{
          transform: `scale(${visible ? 1 : 0})`,
          transitionDuration: `${TRANSITION_MS}ms`,
        }}
      >
        <div className="text-brand-500 mb-4 animate-heartbeat">
          <FontAwesomeIcon icon={faHeart} size="5x" />
        </div>
        <h2 className="text-xl font-semibold">{i18n.t('processing_heading')}</h2>
        <p className="text-gray-600">{i18n.t('processing_subtitle')}</p>
      </div>
    </div>
  )
}

export default ProcessingOverlay
